// `cardsChanged`: announced after a sync pull replaced cards on this device (the other device
// reviewed them, or the server kept its own version of a card this device pushed). A study
// session in progress subscribes and drops the items that are no longer due, so a card
// reviewed elsewhere is not reviewed twice. See app/ARCHITECTURE.md ("Sync events").
import type { CardRow } from '@valyrio/engine/api';
import { cardKey } from './fsrs';

export interface CardsChanged {
  /** Card keys (`${type}|${itemId}`, lib/fsrs.ts cardKey) as stored in the cards map. */
  keys: string[];
  /** The changed cards' item ids (a word id, `${lemmaId}#form` for drills). */
  itemIds: string[];
  /** The cards as they are now on this device. */
  cards: CardRow[];
  source: 'pull';
}

type Listener = (e: CardsChanged) => void;
const listeners = new Set<Listener>();

/** Subscribe; returns the unsubscribe function (call it on unmount). */
export function onCardsChanged(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Announce changed cards (state/actions.ts after a pull). Listeners never break the sync. */
export function emitCardsChanged(cards: readonly CardRow[]): void {
  if (!cards.length || !listeners.size) return;
  const e: CardsChanged = {
    keys: cards.map((c) => cardKey(c.type, c.itemId)),
    itemIds: [...new Set(cards.map((c) => c.itemId))],
    cards: [...cards],
    source: 'pull',
  };
  // a copy: a listener may unsubscribe while this runs
  for (const l of Array.from(listeners)) {
    try {
      l(e);
    } catch (err) {
      console.warn('[cardsChanged] listener failed', err);
    }
  }
}
